/**
 * Lib.Common: Browser Utilities
 */


const haveDomain =
    $build.platform == 'web' ||
    $build.platform == 'marketing' ||
    $build.platform == 'universal';

const txprop =
    $build.platform == 'ios' || $build.platform == 'desktop' || 'transform' in document.documentElement.style
        ? ['transform','transform']
        : ['webkitTransform','-webkit-transform'];

const toprop =
    $build.platform == 'ios' || $build.platform == 'desktop' || 'transformOrigin' in document.documentElement.style
        ? 'transformOrigin'
        : 'webkitTransformOrigin';

// overflow values that support user scrolling
const scrolling = {
    auto: true,
    overlay: true,
    scroll: true
};

const INT_MIN = -2e9;
const INT_MAX = 2e9;


const $util = {

    /**
     * Get, set, or delete a persistent cookie that is valid across IBKR subdomains
     * if running from one.
     *
     * @example
     * // Set IB_LANG to 'ru' and never expire
     * cookie('IB_LANG', 'ru');
     * @example
     * // Set IB_LANG to 'ru' and expire in seven days
     * cookie('IB_LANG', 'ru', 7);
     * @example
     * // Delete IB_LANG
     * cookie('IB_LANG', null);
     */
    cookie(key, val, expires) {
        // Return true if cookie should have secure flag
        const isSecure = key => true;

        if (val !== void 0) {
            if (!(expires instanceof Date)) {
                const e = expires;
                expires = new Date();
                expires.setMilliseconds(expires.getMilliseconds() + (e || (val ? 3652 : -1)) * 864e+5);
            }

            // Strip subdomain and set cookie across IB domain
            if (haveDomain) {
                const domain = location.hostname.replace(/.+(?=\.(ibkr|interactivebrokers)\.)/,'');
                document.cookie =
                    (key + '=' + encodeURIComponent(val || '')) +
                    ('; expires=' + expires.toUTCString()) +
                    ('; path=/') +
                    ('; domain=' + domain) +
                    (isSecure(key) && location.protocol == 'https:' ? '; secure' : '');
            } else {
                document.cookie =
                    (key + '=' + encodeURIComponent(val || '')) +
                    ('; expires=' + expires.toUTCString()) +
                    ('; path=/');
            }
        } else {
            let result;
            return (result = RegExp('(?:^|; )' + key + '=([^;]*)').exec(document.cookie))
                ? decodeURIComponent(result[1])
                : null;
        }
    },


    /* DEPRECATED */
    hasClass(el, name) {
        return el.classList.contains(name);
    },


    /**
     * Retrieve a style object with a CSS transform applied in the most efficient manner
     * compatible with the browser.
     */
    transform(options) {
        let style = {};
        if (options.tx != null || options.ty != null) {
            const tx = typeof options.tx === 'number' ? options.tx + 'px' : options.tx || 0;
            const ty = typeof options.ty === 'number' ? options.ty + 'px' : options.ty || 0;
            style[txprop[0]] = 'translate3d(' + tx + ',' + ty + ',0)';
        } else {
            style[txprop[0]] = '';
        }
        if (options.sx != null || options.sy != null) {
            style[txprop[0]] += ' scale3d(' + (options.sx != null ? options.sx : 1) + ',' + (options.sy != null ? options.sy : 1) + ',1)';
        }
        if (options.r != null) {
            const r = typeof options.r === 'number' ? options.r + 'deg' : options.r || 0;
            style[txprop[0]] += ' rotate3d(0,0,1,' + r + ')';
        }
        return style;
    },


    /** Return true if this element is visible on the page (even if scrolled away, opacity: 0, or visibility: none). */
    isVisible: el => {
        const parent = el.offsetParent;
        if (parent == null) {
            return false;
        }
        if (parent === document.body) { // Maybe IE
            do {
                const style = getComputedStyle(el);
                if (style.display == 'none') {
                    return false;
                }
            } while((el = el.parentNode));
        }
        return true;
    },


    /** Return true if (x,y) in document coordinates is inside element */
    isInside: (el, x, y) => {
        x -= window.pageXOffset || document.documentElement.scrollLeft;
        y -= window.pageYOffset || document.documentElement.scrollTop;

        const rect = el.getBoundingClientRect();
        return x >= rect.left &&
            x <= rect.right &&
            y >= rect.top &&
            y <= rect.bottom;
    },


    /** Return true if this element is a child of the given parent */
    isChild: (el, parent) => {
        while ((el = el.parentNode) && el !== parent);
        return !!el;
    },


    /** Return true if this element is a child of the given parent, or is the given parent */
    isChildOrSelf: (el, parentOrSelf) => {
        while (el !== parentOrSelf && el && (el = el.parentNode));
        return !!el;
    },


    /**
     * Get nearest parent, optionally the nearest one for which fn() is true.
     *
     * @param {Element} el - Element to begin searching from.
     * @param {Function} [fn] - Function which returns `true` if we can stop searching at this element; `false` otherwise.
     * @return {Element} Parent element that has been found; or the `:root` element otherwise.
     */
    parent(el, fn) {
        while ((el = el.parentElement)) {
            if (!fn || fn(el)) {
                return el;
            }
        }
    },


    /**
     * Get previous sibling, optionally the nearest one for which fn() is true.
     *
     * @param {Element} el - Element to begin searching from.
     * @param {Function} [fn] - Function which returns `true` if we can stop searching at this element; `false` otherwise.
     * @return {Element} Sibling element that has been found; or `null` otherwise.
     */
    previous(el, fn) {
        while ((el = el.previousElementSibling)) {
            if (!fn || fn(el)) {
                return el;
            }
        }
        return null;
    },


    /**
     * Get next sibling, optionally the nearest one for which fn() is true.
     *
     * @param {Element} el - Element to begin searching from.
     * @param {Function} [fn] - Function which returns `true` if we can stop searching at this element; `false` otherwise.
     * @return {Element} Sibling element that has been found; or `null` otherwise.
     */
    next(el, fn) {
        while ((el = el.nextElementSibling)) {
            if (!fn || fn(el)) {
                return el;
            }
        }
        return null;
    },


    /**
     * Retrieve computed CSS value.
     */
    css(el, prop) {
        if (el.nodeType == 1) {
            return getComputedStyle(el).getPropertyValue(prop);
        }
    },


    /**
     * Retrieve absolute bounding rectangle of element or array of elements relative to
     * current viewport.
     *
     * @param {Element|Array} el - Element whose bounding rectangle we wish to calculate.
     * @param {Element} invert - Element whose CSS transform we do not wish to consider
     * in the computed rectangle, if any. This element must either be `el` or a parent of `el`.
     */
    boundingRect(el, invert) {
        if (el === window) {
            el = document.body;
            const rect = el.getBoundingClientRect();
            const style = getComputedStyle(el);
            return {
                top: parseFloat(style.getPropertyValue('padding-top')),
                right: rect.right - rect.left,
                bottom: rect.bottom - rect.top,
                left: parseFloat(style.getPropertyValue('padding-left'))
            };
        } else if (el instanceof Array) {
            let r = {
                top: INT_MAX,
                right: INT_MIN,
                bottom: INT_MIN,
                left: INT_MAX
            };
            for (let i = 0; i < el.length; i++) {
                const rect = $util.boundingRect(el[i]);
                if (r.top > rect.top) {
                    r.top = rect.top;
                }
                if (r.right < rect.right) {
                    r.right = rect.right;
                }
                if (r.bottom < rect.bottom) {
                    r.bottom = rect.bottom;
                }
                if (r.left > rect.left) {
                    r.left = rect.left;
                }
                r.width = r.right - r.left;
                r.height = r.bottom - r.top;
            }
            return r;
        } else {
            const r = (el === window ? document.body : el).getBoundingClientRect();
            if (invert && invert !== window) {
                const s = getComputedStyle(invert);
                const tx = s[txprop[0]];
                if (tx && tx != 'none') {
                    // Invert transform
                    const tr = el === invert ? r : invert.getBoundingClientRect();
                    const tm = new DOMMatrix(tx);
                    const to = s[toprop].split(' ').map(parseFloat);
                    const x = tr.x - tm.m41 - (1 - tm.m11) * to[0] + (r.x - tr.x) / tm.m11;
                    const y = tr.y - tm.m42 - (1 - tm.m22) * to[1] + (r.y - tr.y) / tm.m22;
                    const w = r.width / tm.m11;
                    const h = r.height / tm.m22;
                    return {
                        x, y, width: w, height: h, top: y, right: x + w, bottom: y + h, left: x
                    };
                }
            }
            return r;
        }
    },


    /**
     * Get the nearest parent element whose scrolling can affect specified element's position, if any.
     */
    scrollParent(el) {
        const root = document.documentElement;
        while ((el = el.parentElement) && (el !== root)) {
            const style = getComputedStyle(el);
            if (
                scrolling[style.getPropertyValue('overflow-x')] === true ||
                scrolling[style.getPropertyValue('overflow-y')] === true) {
                return el === document.body ? window : el;
            }
            if (style.getPropertyValue('position') === 'fixed') {
                break;
            }
        }
    },


    /**
     * Get array of parent elements whose scrolling can affect specified element's position.
     */
    scrollParents(el) {
        let r = [];
        const root = document.documentElement;
        while ((el = el.parentElement) && (el !== root)) {
            const style = getComputedStyle(el);
            if (
                scrolling[style.getPropertyValue('overflow-x')] === true ||
                scrolling[style.getPropertyValue('overflow-y')] === true) {
                r.unshift(el === document.body ? window : el);
            }
            if (style.getPropertyValue('position') === 'fixed') {
                break;
            }
        }
        return r;
    },


    /**
     * Scroll a parent element (by default, the nearest parent with a scrollbar)
     * the minimum amount to bring this element fully into view, with optional margin.
     */
    scrollIntoView(el, viewport = $util.scrollParent(el), invert = viewport, margin = {}, behavior) {
        if (el && viewport) {
            if (viewport === document.body || viewport === document.documentElement) {
                viewport = window;
            }
            const erect = $util.boundingRect(el, invert);
            const vrect = $util.boundingRect(viewport, invert);

            const dl = Math.min(0, erect.left - (margin.left||0) - vrect.left);
            const dr = Math.max(0, erect.right + (margin.right||0) - vrect.right);
            const dx = (!dl ^ !dr) ? (dl || dr) : 0;

            const dt = Math.min(0, erect.top - (margin.top||0) - vrect.top);
            const db = Math.max(0, erect.bottom + (margin.bottom||0) - vrect.bottom);
            const dy = (!dt ^ !db) ? (dt || db) : 0;

            if (dx || dy) {
                viewport.scrollBy({
                    left: dx,
                    top: dy,
                    behavior
                });
            }
        }
    },


    /**
     * Scroll a parent element (by default, the nearest parent with a scrollbar)
     * so that this element is centered in view.
     */
    scrollIntoCenter(el, viewport = $util.scrollParent(el), behavior) {
        if (el && viewport) {
            if (viewport === document.body || viewport === document.documentElement) {
                viewport = window;
            }
            const erect = $util.boundingRect(el);
            const vrect = $util.boundingRect(viewport);

            const top = erect.top - vrect.top + viewport.scrollTop - (vrect.height - erect.height) / 2;
            const left = erect.left - vrect.left + viewport.scrollLeft - (vrect.width - erect.width) / 2;

            viewport.scrollTo({
                left: Math.min(Math.max(left, 0), viewport.scrollWidth - vrect.width),
                top: Math.min(Math.max(top, 0), viewport.scrollHeight - vrect.height),
                behavior
            });
        }
    },


    /**
     * Retrieve an array of all child elements that can receive tab focus, in their tab order.
     */
    tabbableChildren(el) {
        return [].map.call(el.querySelectorAll([
            'input',
            'select',
            'a[href]',
            'textarea',
            'button',
            '[tabindex]'
        ]), (el,i) => ({ el,i })).
            filter(e =>
                e.el.tabIndex >= 0 && !e.el.disabled && $util.isVisible(e.el)).
            sort((a,b) =>
                a.el.tabIndex === b.el.tabIndex ? a.i - b.i : (a.el.tabIndex || INT_MAX) - (b.el.tabIndex || INT_MAX));
    },


    /**
     * Retrieve the Vue component associated with an element.
     * WARNING: This accesses a private VueJS property which could potentiall change in a future release.
     */
    component(el) {
        return el.__vue__;
    }
}


export default $util;
